import { useMemo, useState } from 'react';
import { Plus, Webhook, ChevronDown } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWaapDiscoveryRules,
  useCreateWaapDiscoveryRule,
  useUpdateWaapDiscoveryRule,
  useDeleteWaapDiscoveryRule,
  useWaapDiscoverySampling,
  useSaveWaapDiscoverySampling,
  useAdvancedEnabledSites,
  type WaapDiscoveryRule,
  type WaapDiscoveryCondition,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { useWaapUpgradeGate } from '@/pages/services/waap/use-waap-upgrade-gate';
import styles from './ApiProtectionDiscoveryPage.module.css';

// Triggering-condition metric (vendor `key`). domain is inferred from the form's
// default; the rest are confirmed from captured rules.
const METRICS = [
  { value: 'domain', label: 'Domain' },
  { value: 'path', label: 'Path' },
  { value: 'ua', label: 'User Agent' },
  { value: 'request_header', label: 'Request Header' },
  { value: 'http_method', label: 'HTTP Method' },
  { value: 'response_content_type', label: 'Response Content Type' },
];
// Matching condition (vendor `condition`). contain/equal/not_contain are confirmed.
const MATCHES = [
  { value: 'equal', label: 'Equal to' },
  { value: 'contain', label: 'Contains' },
  { value: 'not_contain', label: 'Does not contain' },
];
const FREQ = [
  { value: '60', label: 'Every hour' },
  { value: '1440', label: 'Every day' },
];

const metricLabel = (k: string) => METRICS.find((m) => m.value === k)?.label ?? k;
const matchLabel = (c: string) => MATCHES.find((m) => m.value === c)?.label ?? c;
const condSummary = (r: WaapDiscoveryRule) =>
  r.conditions.map((c) => `${metricLabel(c.key)} ${matchLabel(c.condition)} ${c.value}`).join('; ') || '—';

const emptyRule = (): WaapDiscoveryRule => ({
  id: 0,
  name: '',
  type: 2,
  description: '',
  exclude_static_files: true,
  exclude_ajax: false,
  conditions: [],
});

/** Custom Rules — list + add/edit (inline) + delete. Shows only custom (type 2). */
function CustomRulesCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { gate, upgradeDialog } = useWaapUpgradeGate();
  const { data, isLoading } = useWaapDiscoveryRules(domainId);
  // Domain metric: the Value is one or more enabled-site domains (GET /sites/enabled),
  // stored comma-joined to match the vendor (e.g. "a.com,b.com").
  const { data: enabledSites = [] } = useAdvancedEnabledSites(domainId);
  const enabledDomains = useMemo(() => enabledSites.map((s) => s.domain), [enabledSites]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<WaapDiscoveryRule>(emptyRule());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WaapDiscoveryRule | null>(null);
  // Draft condition row (Metrics / Matching / Value + Add).
  const [cond, setCond] = useState<WaapDiscoveryCondition>({ key: 'domain', condition: 'equal', value: '' });
  const [condEditIdx, setCondEditIdx] = useState<number | null>(null);

  const rules = useMemo(() => (data ?? []).filter((r) => r.type === 2), [data]);

  const close = () => {
    setOpen(false);
    setEditingId(null);
    setForm(emptyRule());
    setCond({ key: 'domain', condition: 'equal', value: '' });
    setCondEditIdx(null);
  };
  const startAdd = () => {
    setEditingId(null);
    setForm(emptyRule());
    setOpen(true);
  };
  const startEdit = (r: WaapDiscoveryRule) => {
    setEditingId(r.id);
    setForm({ ...r, conditions: [...r.conditions] });
    setOpen(true);
  };

  const create = useCreateWaapDiscoveryRule(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Custom rule created' }); close(); },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateWaapDiscoveryRule(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Custom rule updated' }); close(); },
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const remove = useDeleteWaapDiscoveryRule(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Custom rule deleted' }); setPendingDelete(null); },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const addCondition = () => {
    if (!cond.value.trim()) return;
    const entry = { ...cond, value: cond.value.trim() };
    setForm((f) => ({
      ...f,
      conditions:
        condEditIdx === null
          ? [...f.conditions, entry]
          : f.conditions.map((c, i) => (i === condEditIdx ? entry : c)),
    }));
    setCond({ key: 'domain', condition: 'equal', value: '' });
    setCondEditIdx(null);
  };
  const editCondition = (i: number) => {
    setCond({ ...form.conditions[i] });
    setCondEditIdx(i);
  };
  const removeCondition = (i: number) => {
    setForm((f) => ({ ...f, conditions: f.conditions.filter((_, idx) => idx !== i) }));
    setCondEditIdx((cur) => (cur === i ? null : cur));
  };

  const valid = form.name.trim().length > 0 && form.conditions.length > 0;
  const saving = create.isPending || update.isPending;
  const submit = () => {
    const body: WaapDiscoveryRule = { ...form, name: form.name.trim(), description: form.description.trim() };
    if (editingId) update.mutate({ id: editingId, body });
    else create.mutate(body);
  };

  return (
    <Card>
      <CardHeader
        actions={
          <Button
            variant="secondary"
            size="sm"
            startIcon={<Plus size={14} />}
            onClick={gate('apiProtection', 'API Protection', startAdd)}
          >
            Add Custom Rule
          </Button>
        }
      >
        <CardTitle>Custom Rules</CardTitle>
      </CardHeader>
      <CardBody className={styles.bodyGrid}>
        {isLoading ? (
          <Skeleton height={80} />
        ) : rules.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<Webhook size={20} />}
            title="No custom rules"
            description="No custom API-discovery rules on this website yet."
          />
        ) : (
          <div className={styles.rulesScroll}><div className={styles.rules}>
            <div className={styles.ruleHead}>
              <span>ID</span>
              <span>Rule Name</span>
              <span>Description</span>
              <span>Triggering Condition</span>
              <span>Operation</span>
            </div>
            {rules.map((r) => (
              <div key={r.id} className={styles.ruleRow}>
                <span className={styles.muted}>{r.id}</span>
                <span className={styles.name}>{r.name}</span>
                <span className={styles.muted}>{r.description || '—'}</span>
                <span>{condSummary(r)}</span>
                <div className={styles.actions}>
                  <Button variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => setPendingDelete(r)}>Delete</Button>
                </div>
              </div>
            ))}
          </div></div>
        )}

        {open && (
          <div className={styles.form}>
            <Field label="Name" required description="1-26 chars: 0-9 A-Z a-z space . _ - ( ) * +">
              {(p) => <Input {...p} value={form.name} placeholder="User_API_Rule" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />}
            </Field>
            <Field label="Description" description="Up to 200 characters.">
              {(p) => <Input {...p} value={form.description} maxLength={200} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />}
            </Field>

            <div className={styles.checkRow}>
              <label className={styles.checkLabel}>
                <Checkbox checked={form.exclude_static_files} onCheckedChange={(v) => setForm((f) => ({ ...f, exclude_static_files: Boolean(v) }))} />
                Exclude static files
              </label>
              <label className={styles.checkLabel}>
                <Checkbox checked={form.exclude_ajax} onCheckedChange={(v) => setForm((f) => ({ ...f, exclude_ajax: Boolean(v) }))} />
                Exclude AJAX requests
              </label>
            </div>

            <div className={styles.condSection}>
              <div className={styles.sectionLabel}>Triggering Condition</div>
              <div className={styles.condRow}>
                <Select value={cond.key} onValueChange={(v) => setCond((c) => ({ ...c, key: v, value: '' }))}>
                  <SelectTrigger className={styles.metricSelect}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={cond.condition} onValueChange={(v) => setCond((c) => ({ ...c, condition: v }))}>
                  <SelectTrigger className={styles.condSelect}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MATCHES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {cond.key === 'domain' ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="secondary"
                        className={styles.condValue}
                        style={{ justifyContent: 'space-between', fontWeight: 'var(--vn-font-weight-regular)' }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cond.value || 'Select domains'}
                        </span>
                        <ChevronDown size={14} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)', maxHeight: 240, overflowY: 'auto' }}>
                        {enabledDomains.length === 0 ? (
                          <span className={styles.muted}>No websites</span>
                        ) : (
                          enabledDomains.map((d) => {
                            const set = new Set(cond.value.split(',').map((s) => s.trim()).filter(Boolean));
                            return (
                              <label key={d} style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
                                <Checkbox
                                  checked={set.has(d)}
                                  onCheckedChange={(v) => {
                                    const next = new Set(set);
                                    if (v) next.add(d);
                                    else next.delete(d);
                                    setCond((c) => ({ ...c, value: [...next].join(',') }));
                                  }}
                                />
                                <span>{d}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Input className={styles.condValue} placeholder="/v1/user/" value={cond.value} onChange={(e) => setCond((c) => ({ ...c, value: e.target.value }))} />
                )}
                <Button variant="secondary" size="sm" onClick={addCondition} disabled={!cond.value.trim()}>{condEditIdx === null ? 'Add' : 'Save'}</Button>
              </div>

              {form.conditions.length > 0 && (
                <div className={styles.condList}>
                  {form.conditions.map((c, i) => (
                    <div key={i} className={styles.condItem}>
                      <span>{metricLabel(c.key)}</span>
                      <span className={styles.muted}>{matchLabel(c.condition)}</span>
                      <span>{c.value}</span>
                      <div className={styles.actions}>
                        <Button variant="ghost" size="sm" onClick={() => editCondition(i)}>Edit</Button>
                        <Button variant="ghost" size="sm" onClick={() => removeCondition(i)}>Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <Button variant="secondary" onClick={close}>Cancel</Button>
              <Button disabled={!valid || saving} onClick={submit}>
                {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </CardBody>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete custom rule?"
        description={`Remove "${pendingDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
      {upgradeDialog}
    </Card>
  );
}

/** API Request/Response Sampling Frequency — enable + cadence. */
function SamplingCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { gate, upgradeDialog } = useWaapUpgradeGate();
  const { data, isLoading } = useWaapDiscoverySampling(domainId);
  const [enabled, setEnabled] = useState(false);
  const [freq, setFreq] = useState('60');
  const [touched, setTouched] = useState(false);

  // Seed local state from the server once loaded (until the user edits).
  const v = touched ? { enabled, frequency: freq } : (data ?? { enabled: false, frequency: '60' });

  const save = useSaveWaapDiscoverySampling(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Sampling saved' }); setTouched(false); },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const edit = (next: { enabled: boolean; frequency: string }) => {
    setEnabled(next.enabled);
    setFreq(next.frequency);
    setTouched(true);
  };

  return (
    <Card>
      <CardHeader><CardTitle>API Request/Response Sampling Frequency</CardTitle></CardHeader>
      <CardBody className={styles.bodyGrid}>
        {isLoading ? (
          <Skeleton height={48} />
        ) : (
          <>
            <div className={styles.samplingRow}>
              <label className={styles.checkLabel}>
                <Checkbox checked={v.enabled} onCheckedChange={(c) => edit({ enabled: Boolean(c), frequency: v.frequency })} />
                Enable
              </label>
              {v.enabled && (
                <Select value={v.frequency} onValueChange={(val) => edit({ enabled: v.enabled, frequency: val })}>
                  <SelectTrigger className={styles.freqSelect}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQ.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className={styles.formActions}>
              <Button
                disabled={!touched || save.isPending}
                onClick={gate('apiProtection', 'API Protection', () =>
                  save.mutate({ enabled: v.enabled, frequency: v.frequency }),
                )}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        )}
      </CardBody>
      {upgradeDialog}
    </Card>
  );
}

/**
 * API Protection → Discovery (Settings). Custom discovery rules + the API
 * request/response sampling cadence, backed by the private waap2 portal.
 * Account-level: the selected website only resolves the Conversant customer.
 */
export function ApiProtectionDiscoveryPage() {
  // Account-level: any of the account's websites resolves the same customer, so
  // a representative domain is used automatically (no picker / select-website gate).
  const { selected, isLoading: wsLoading } = useWaapWebsite();
  const domainId = selected?.domainId ?? '';

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'API Protection' }, { label: 'Discovery' }]} />}
        title="Settings"
      />
      {wsLoading ? (
        <Skeleton height={320} />
      ) : (
        <Section title="Discovery" description="Auto-discover APIs via custom rules and request/response sampling.">
          <div className={styles.stack}>
            <CustomRulesCard domainId={domainId} />
            <SamplingCard domainId={domainId} />
          </div>
        </Section>
      )}
    </Container>
  );
}
